/**
 * Authentication API
 * 
 * This module provides authentication-related API calls including
 * login, registration, TOTP verification, and permission management.
 */

import apiClient from './client';

// Types
interface ApiEndpoints {
  REGISTER: string;
  LOGIN: string;
  LOGOUT: string;
  REFRESH_TOKEN: string;
  GENERATE_TOTP: string;
  VERIFY_TOTP: string;
  FORGOT_PASSWORD: string;
  VERIFY_RESET_CODE: string;
  RESET_PASSWORD: string;
  ME: string;
  UPDATE_PROFILE: string;
  CHANGE_PASSWORD: string;
  PERMISSIONS: string;
  VALUE_CHAINS: string;
  COUNTRIES: string;
  DATA_PERMISSIONS: string;
  HEALTH: string;
}

interface UserData {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  company?: string;
}

interface LoginData {
  email: string;
  password: string;
}

interface TOTPData {
  email: string;
  totp_code: string;
  temp_token?: string;
}

interface GenerateTOTPData {
  email: string;
  password?: string;
  temp_token?: string;
}

interface ForgotPasswordData {
  email: string;
}

interface VerifyResetCodeData {
  email: string;
  temp_token: string;
  totp_code: string;
}

interface VerifyResetCodeResponse {
  success: boolean;
  message: string;
  verified_token?: string;
  expires_in: number;
}

interface ResetPasswordData {
  email: string;
  temp_token?: string;
  totp_code?: string;
  verified_token?: string;
  new_password: string;
}

interface ForgotPasswordResponse {
  success: boolean;
  message: string;
  temp_token?: string;
  expires_in: number;
}

interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

interface User {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name: string;
  company?: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  last_login?: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

interface ValueChainAccess {
  id: number;
  value_chain_id: string;
  value_chain_name: string;
  permission_level: string;
  is_active: boolean;
  granted_at: string;
  expires_at?: string;
  granted_by?: number;
  notes?: string;
}

interface CountryAccess {
  id: number;
  country_code: string;
  country_name: string;
  permission_level: string;
  is_active: boolean;
  granted_at: string;
  expires_at?: string;
  granted_by?: number;
  notes?: string;
}

interface DataPermission {
  id: number;
  data_type: string;
  data_identifier: string;
  permission_level: string;
  is_active: boolean;
  granted_at: string;
  expires_at?: string;
  data_metadata?: any;
  notes?: string;
}

interface UserPermissions {
  user_id: number;
  email: string;
  value_chain_access: ValueChainAccess[];
  country_access: CountryAccess[];
  data_permissions: DataPermission[];
}

interface AuthResponse {
  success: boolean;
  user?: User;
  tokens?: TokenResponse;
  permissions?: UserPermissions;
  message?: string;
  totp_required?: boolean;
  temp_token?: string;
  expires_in?: number;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  details?: any;
}

// API endpoints
const ENDPOINTS: ApiEndpoints = {
  // Authentication
  REGISTER: '/api/v1/auth/register',
  LOGIN: '/api/v1/auth/login',
  LOGOUT: '/api/v1/auth/logout',
  REFRESH_TOKEN: '/api/v1/auth/refresh-token',
  
  // TOTP
  GENERATE_TOTP: '/api/v1/auth/generate-totp',
  VERIFY_TOTP: '/api/v1/auth/verify-totp',

  // Password reset
  FORGOT_PASSWORD: '/api/v1/auth/forgot-password',
  VERIFY_RESET_CODE: '/api/v1/auth/verify-reset-code',
  RESET_PASSWORD: '/api/v1/auth/reset-password',

  // User management
  ME: '/api/v1/auth/me',
  UPDATE_PROFILE: '/api/v1/auth/profile',
  CHANGE_PASSWORD: '/api/v1/auth/change-password',
  
  // Permissions
  PERMISSIONS: '/api/v1/auth/permissions',
  VALUE_CHAINS: '/api/v1/auth/value-chains',
  COUNTRIES: '/api/v1/auth/countries',
  DATA_PERMISSIONS: '/api/v1/auth/data-permissions',
  
  // Health check
  HEALTH: '/api/v1/health',
};

/**
 * Authentication API Class
 */
class AuthAPI {
  private client: typeof apiClient;

  constructor() {
    this.client = apiClient;
  }

  /**
   * Register a new user
   */
  async register(userData: UserData): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await this.client.request(ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Registration failed',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Login user
   */
  async login(loginData: LoginData): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await this.client.request(ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'Login failed';

        // Backend now returns error messages in 'message' field for auth endpoints
        // Fallback to 'detail' for compatibility with other endpoints
        const backendMessage = errorData?.message || errorData?.detail;
        
        if (response.status === 401) {
          // Prefer backend-provided message (e.g., 'Incorrect password')
          if (backendMessage) {
            errorMessage = backendMessage;
          } else if (errorData?.error_code === 'INCORRECT_PASSWORD') {
            errorMessage = 'Incorrect password';
          } else {
            errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          }
        } else if (response.status === 404) {
          // User not found - backend now returns "User not found" message
          if (backendMessage) {
            errorMessage = backendMessage;
          } else if (errorData?.error_code === 'USER_NOT_FOUND') {
            errorMessage = 'User not found';
          } else {
            errorMessage = 'User not found';
          }
        } else if (response.status === 422) {
          errorMessage = backendMessage || 'Please check your email and password format.';
        } else if (response.status === 423) {
          errorMessage = backendMessage || 'User account is temporarily locked';
        } else if (response.status === 429) {
          errorMessage = backendMessage || 'Too many login attempts. Please wait a moment before trying again.';
        } else if (response.status >= 500) {
          errorMessage = backendMessage || 'Server error. Please try again later.';
        } else if (backendMessage) {
          errorMessage = backendMessage;
        }
        
        return {
          success: false,
          error: errorMessage,
          details: errorData,
        };
      }
      
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      let errorMessage = 'Login failed';
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage,
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await this.client.authenticatedPost(ENDPOINTS.LOGOUT);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Logout failed',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Generate TOTP code
   */
  async generateTOTP(totpData: GenerateTOTPData): Promise<ApiResponse<{ message: string; expires_in: number; remaining_time: number }>> {
    try {
      console.log('AuthAPI - Generating TOTP for:', totpData.email);
      console.log('AuthAPI - Endpoint:', ENDPOINTS.GENERATE_TOTP);
      const response = await this.client.request(ENDPOINTS.GENERATE_TOTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(totpData),
      });
      
      console.log('AuthAPI - Generate TOTP response status:', response.status, response.ok);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'TOTP generation failed';
        
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (response.status === 401) {
          errorMessage = 'Invalid password';
        } else if (response.status === 404) {
          errorMessage = 'User not found';
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        console.log('AuthAPI - Generate TOTP error:', errorMessage);
        return {
          success: false,
          error: errorMessage,
          details: errorData,
        };
      }
      
      const data = await response.json();
      console.log('AuthAPI - Generate TOTP success:', data);
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      console.error('AuthAPI - Generate TOTP exception:', error);
      return {
        success: false,
        error: error.message || 'TOTP generation failed',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Verify TOTP code
   */
  async verifyTOTP(totpData: TOTPData): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await this.client.request(ENDPOINTS.VERIFY_TOTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(totpData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'TOTP verification failed';
        
        console.log('TOTP API - Error response:', {
          status: response.status,
          errorData,
          hasMessage: !!errorData.message,
          hasDetail: !!errorData.detail,
        });
        
        // Prefer backend error message if available (check both 'message' and 'detail')
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (response.status === 401) {
          errorMessage = 'Invalid TOTP code. Please check the 6-digit code and try again.';
        } else if (response.status === 422) {
          errorMessage = 'Invalid TOTP code format. Please enter a valid 6-digit code.';
        } else if (response.status === 429) {
          errorMessage = 'Too many TOTP attempts. Please wait before trying again.';
        } else if (response.status === 410) {
          errorMessage = 'TOTP session expired. Please log in again.';
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        console.log('TOTP API - Returning error:', { success: false, error: errorMessage });
        
        return {
          success: false,
          error: errorMessage,
          details: errorData,
        };
      }
      
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      let errorMessage = 'TOTP verification failed';
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage,
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Initiate password reset - sends TOTP code to email
   */
  async forgotPassword(data: ForgotPasswordData): Promise<ApiResponse<ForgotPasswordResponse>> {
    try {
      const response = await this.client.request(ENDPOINTS.FORGOT_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'Failed to initiate password reset';

        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (response.status === 429) {
          errorMessage = 'Too many password reset requests. Please wait before trying again.';
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }

        return {
          success: false,
          error: errorMessage,
          details: errorData,
        };
      }

      const responseData = await response.json();

      return {
        success: true,
        data: responseData,
      };
    } catch (error: any) {
      let errorMessage = 'Failed to initiate password reset';

      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Verify TOTP code for password reset (before entering new password)
   */
  async verifyResetCode(data: VerifyResetCodeData): Promise<ApiResponse<VerifyResetCodeResponse>> {
    try {
      const response = await this.client.request(ENDPOINTS.VERIFY_RESET_CODE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'Failed to verify code';

        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (response.status === 401) {
          errorMessage = 'Invalid or expired verification code. Please try again.';
        } else if (response.status === 429) {
          errorMessage = 'Too many verification attempts. Please wait before trying again.';
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }

        return {
          success: false,
          error: errorMessage,
          details: errorData,
        };
      }

      const responseData = await response.json();

      return {
        success: true,
        data: responseData,
      };
    } catch (error: any) {
      let errorMessage = 'Failed to verify code';

      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Complete password reset with TOTP verification
   */
  async resetPassword(data: ResetPasswordData): Promise<ApiResponse<ResetPasswordResponse>> {
    try {
      const response = await this.client.request(ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'Failed to reset password';

        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (response.status === 401) {
          errorMessage = 'Invalid or expired verification code. Please try again.';
        } else if (response.status === 400) {
          errorMessage = 'Password does not meet requirements.';
        } else if (response.status === 404) {
          errorMessage = 'User not found.';
        } else if (response.status === 429) {
          errorMessage = 'Too many password reset attempts. Please wait before trying again.';
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }

        return {
          success: false,
          error: errorMessage,
          details: errorData,
        };
      }

      const responseData = await response.json();

      return {
        success: true,
        data: responseData,
      };
    } catch (error: any) {
      let errorMessage = 'Failed to reset password';

      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<ApiResponse<{ user: User }>> {
    try {
      const response = await this.client.authenticatedGet(ENDPOINTS.ME);
      const data = await response.json();
      
      return {
        success: true,
        data: {
          user: data,
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get user profile',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData: Partial<User>): Promise<ApiResponse<{ user: User }>> {
    try {
      const response = await this.client.authenticatedPut(ENDPOINTS.UPDATE_PROFILE, profileData);
      const data = await response.json();
      
      return {
        success: true,
        data: {
          user: data,
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update profile',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Change password
   */
  async changePassword(passwordData: { current_password: string; new_password: string }): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await this.client.authenticatedPost(ENDPOINTS.CHANGE_PASSWORD, passwordData);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to change password',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Get user permissions
   */
  async getPermissions(): Promise<ApiResponse<UserPermissions>> {
    try {
      const response = await this.client.authenticatedGet(ENDPOINTS.PERMISSIONS);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get permissions',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Get value chain access
   */
  async getValueChainAccess(params: { page?: number; per_page?: number; is_active?: boolean; permission_level?: string; search?: string } = {}): Promise<ApiResponse<{ value_chains: ValueChainAccess[]; total: number; page: number; per_page: number }>> {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
      
      const url = `${ENDPOINTS.VALUE_CHAINS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.client.authenticatedGet(url);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get value chain access',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Get country access
   */
  async getCountryAccess(params: { page?: number; per_page?: number; is_active?: boolean; permission_level?: string; search?: string } = {}): Promise<ApiResponse<{ countries: CountryAccess[]; total: number; page: number; per_page: number }>> {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
      
      const url = `${ENDPOINTS.COUNTRIES}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.client.authenticatedGet(url);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get country access',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Get data permissions
   */
  async getDataPermissions(params: { page?: number; per_page?: number; is_active?: boolean; permission_level?: string; data_type?: string; search?: string } = {}): Promise<ApiResponse<{ permissions: DataPermission[]; total: number; page: number; per_page: number }>> {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
      
      const url = `${ENDPOINTS.DATA_PERMISSIONS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.client.authenticatedGet(url);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get data permissions',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Grant value chain access
   */
  async grantValueChainAccess(accessData: { value_chain_id: string; value_chain_name: string; permission_level: string; expires_at?: string; notes?: string }): Promise<ApiResponse<{ success: boolean; message: string; permission_type: string; permission_id: string }>> {
    try {
      const response = await this.client.authenticatedPost(ENDPOINTS.VALUE_CHAINS + '/grant', accessData);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to grant value chain access',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Grant country access
   */
  async grantCountryAccess(accessData: { country_code: string; country_name: string; permission_level: string; expires_at?: string; notes?: string }): Promise<ApiResponse<{ success: boolean; message: string; permission_type: string; permission_id: string }>> {
    try {
      const response = await this.client.authenticatedPost(ENDPOINTS.COUNTRIES + '/grant', accessData);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to grant country access',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Grant data permission
   */
  async grantDataPermission(permissionData: { data_type: string; data_identifier: string; permission_level: string; expires_at?: string; metadata?: string; notes?: string }): Promise<ApiResponse<{ success: boolean; message: string; permission_type: string; permission_id: string }>> {
    try {
      const response = await this.client.authenticatedPost(ENDPOINTS.DATA_PERMISSIONS + '/grant', permissionData);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to grant data permission',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<ApiResponse<{ status: string; api: string }>> {
    try {
      const response = await this.client.request(ENDPOINTS.HEALTH);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Health check failed',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const accessToken = this.client.getAccessToken();
    const refreshToken = this.client.getRefreshToken();
    
    console.log('AuthAPI - isAuthenticated check:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      accessToken: accessToken ? accessToken.substring(0, 20) + '...' : 'No access token',
      refreshToken: refreshToken ? refreshToken.substring(0, 20) + '...' : 'No refresh token'
    });
    
    return accessToken && !this.client.isTokenExpired(accessToken);
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return this.client.getAccessToken();
  }

  /**
   * Get current refresh token
   */
  getRefreshToken(): string | null {
    return this.client.getRefreshToken();
  }

  /**
   * Clear all tokens
   */
  clearTokens(): void {
    this.client.clearTokens();
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token?: string): boolean {
    return this.client.isTokenExpired(token);
  }

  /**
   * Get user profile and permissions
   */
  async getUserProfileAndPermissions(): Promise<ApiResponse<{ user: User; permissions: UserPermissions }>> {
    try {
      console.log('AuthAPI - Getting user profile from:', ENDPOINTS.ME);
      const userResponse = await this.client.authenticatedGet(ENDPOINTS.ME);
      const userData = await userResponse.json();
      
      console.log('AuthAPI - User profile response:', userData);
      
      console.log('AuthAPI - Getting user permissions from:', ENDPOINTS.PERMISSIONS);
      const permissionsResponse = await this.client.authenticatedGet(ENDPOINTS.PERMISSIONS);
      const permissionsData = await permissionsResponse.json();
      
      console.log('AuthAPI - Permissions response:', permissionsData);
      
      return {
        success: true,
        data: {
          user: userData,
          permissions: permissionsData,
        },
      };
    } catch (error: any) {
      console.error('Get user profile error:', error);
      return {
        success: false,
        error: error.message || 'Failed to get user profile and permissions',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Get error details from response
   */
  private async getErrorDetails(response: Response): Promise<any> {
    try {
      return await response.json();
    } catch {
      return { status: response.status, statusText: response.statusText };
    }
  }
}

// Create and export singleton instance
const authAPI = new AuthAPI();
export { authAPI };
export default authAPI;
